import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
import PrintIcon from "@mui/icons-material/Print";
import { useDispatch } from "react-redux";
import {
  setloadeActive,
  setloadeDeactive,
} from "../../../../../redux/loader/loaderActions";
import baseUrl from "../../../../../api/baseUrl";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { ModalOverlay } from "../../../../../components/ModalOverlay";
import {
  PAPER_SIZES,
  PaperSize,
  REPORT_SECTIONS,
} from "../../../../setting/manageprint/patientReport/patientReportDefaults";
import {
  applyPatientReportGenerateOptions,
  findPatientReportTemplate,
  hydratePatientReportStore,
  loadPatientReportStore,
  savePatientReportStore,
  reportPageCount,
  visibleReportSections,
  buildPatientReportPrintData,
  downloadPatientReportPdf,
  printPatientReportSheet,
} from "../../../../setting/manageprint/patientReport/patientReportStorage";
import {
  CertificateTemplate,
  getCssAtPageSize,
} from "../../../../setting/manageprint/certificatePrint/certificateTypes";
import CertificateCanvas from "../../../../setting/manageprint/certificatePrint/CertificateCanvas";
import { useLoggedInClinic } from "../../../../setting/manageprint/certificatePrint/useLoggedInClinic";
import { resolveCertificateLogo } from "../../../../setting/manageprint/certificatePrint/getLoggedInClinic";
import {
  mergePatientReportPreviewData,
  PATIENT_REPORT_SAMPLE_CHARTS,
  PATIENT_REPORT_SAMPLE_CHECKLISTS,
  PATIENT_REPORT_SAMPLE_DATA,
  PATIENT_REPORT_SAMPLE_TABLES,
} from "../../../../setting/manageprint/patientReport/patientReportSampleData";
import "../../../../setting/manageprint/certificatePrint/certificatePrint.css";

const PAPER_SIZE_STORAGE_KEY = "reportPaperSize";

function waitForPaint() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function ReportModal({ setReportOpen }: any) {
  const dispatch = useDispatch();
  const { ID } = useParams();
  const clinic = useLoggedInClinic();
  const paperRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<string[]>(["1", "6"]);
  const [paperSize, setPaperSize] = useState<PaperSize>("A4");
  const [baseTemplate, setBaseTemplate] = useState<CertificateTemplate | null>(null);
  const [liveData, setLiveData] = useState<ReturnType<typeof buildPatientReportPrintData> | null>(
    null
  );
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    baseUrl
      .get("/clinic/getBillData")
      .then((response) => {
        if (cancelled) return;
        const saved = response?.data?.data?.patientReportPrint;
        const store = saved
          ? hydratePatientReportStore(saved)
          : loadPatientReportStore();
        // Prefer clinic default / last active — not the first A4 builtin.
        const template = findPatientReportTemplate(
          store.templates,
          store.defaultId || store.activeId
        );
        try {
          savePatientReportStore(store);
        } catch {
          /* ignore */
        }
        const sections = visibleReportSections(template);
        setBaseTemplate(template);
        setPaperSize(
          template.page.size === "A5"
            ? "A5"
            : template.page.size === "halfLetter"
              ? "halfLetter"
              : "A4"
        );
        if (sections.length) {
          setFormData(Array.from(new Set(sections)));
        }
      })
      .catch(() => {
        if (cancelled) return;
        const store = loadPatientReportStore();
        const template = findPatientReportTemplate(
          store.templates,
          store.defaultId || store.activeId
        );
        setBaseTemplate(template);
        setPaperSize(
          template.page.size === "A5"
            ? "A5"
            : template.page.size === "halfLetter"
              ? "halfLetter"
              : "A4"
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!ID) return;
    let cancelled = false;
    baseUrl
      .get("/client/report/patientReportData", {
        headers: { treatmentid: ID },
      })
      .then((response) => {
        if (cancelled) return;
        const data = response?.data?.data;
        if (!data) return;
        setLiveData(buildPatientReportPrintData(data));
      })
      .catch(() => {
        /* keep sample preview */
      });
    return () => {
      cancelled = true;
    };
  }, [ID]);

  const sourceTemplate = useMemo(() => {
    if (baseTemplate) return baseTemplate;
    const store = loadPatientReportStore();
    return findPatientReportTemplate(store.templates, store.defaultId || store.activeId);
  }, [baseTemplate]);

  const template = useMemo(
    () => applyPatientReportGenerateOptions(sourceTemplate, formData, paperSize),
    [formData, paperSize, sourceTemplate]
  );

  const previewPage = useMemo(
    () => ({
      ...template.page,
      showSafeArea: false,
      logoSrc: resolveCertificateLogo(clinic.printImages, template.page.logoSrc),
      letterheadSrc: template.page.letterheadSrc || "",
    }),
    [clinic.printImages, template.page]
  );

  const samplePreview = useMemo(
    () => mergePatientReportPreviewData(PATIENT_REPORT_SAMPLE_DATA, clinic),
    [clinic]
  );

  const previewData = liveData?.previewData || samplePreview;
  const previewTables = liveData?.previewTables || PATIENT_REPORT_SAMPLE_TABLES;
  const previewCharts = liveData?.previewCharts || PATIENT_REPORT_SAMPLE_CHARTS;
  const previewChecklists =
    liveData?.previewChecklists || PATIENT_REPORT_SAMPLE_CHECKLISTS;

  const handleSectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    const next = checked
      ? [...formData, value]
      : formData.filter((key) => key !== value);
    setFormData(next);
    localStorage.setItem("reportData", JSON.stringify(next));
  };

  const handlePaperSizeChange = (size: PaperSize) => {
    setPaperSize(size);
    localStorage.setItem(PAPER_SIZE_STORAGE_KEY, size);
  };

  const handlePrint = useCallback(async () => {
    if (!formData.length) {
      toast.error("Select at least one section");
      return;
    }
    const paper = paperRef.current;
    if (!paper) {
      toast.error("Report preview is not ready");
      return;
    }
    setPrinting(true);
    await waitForPaint();
    try {
      await printPatientReportSheet(paper);
    } catch {
      toast.error("Could not print the report.");
    } finally {
      setPrinting(false);
    }
  }, [formData.length]);

  const generate = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!formData.length) {
        toast.error("Select at least one section");
        return;
      }
      const paper = paperRef.current;
      if (!paper) {
        toast.error("Report preview is not ready");
        return;
      }
      dispatch(setloadeActive());
      setPrinting(true);
      await waitForPaint();
      try {
        await downloadPatientReportPdf({
          stack: paper,
          size: template.page.size,
          orientation: template.page.orientation,
          filename: `patient-report-${paperSize.toLowerCase()}.pdf`,
        });
      } catch {
        toast.error("Could not generate the customized report.");
      } finally {
        setPrinting(false);
        dispatch(setloadeDeactive());
      }
    },
    [dispatch, formData.length, paperSize, template.page.orientation, template.page.size]
  );

  return (
    <ModalOverlay
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="generate-report-title"
    >
      <style>
        {`@media print { @page { size: ${getCssAtPageSize(template.page.orientation, template.page.size)}; margin: 0; } }`}
      </style>
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between bg-gradient-to-r from-primary to-primaryLight">
          <div className="flex items-center gap-3">
            <DescriptionIcon className="text-white" />
            <h2 id="generate-report-title" className="text-xl font-bold text-white">
              Generate Report
            </h2>
          </div>
          <button
            type="button"
            onClick={() => setReportOpen(false)}
            className="w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        <form onSubmit={generate} className="grid md:grid-cols-[280px_1fr] min-h-0 flex-1 overflow-hidden">
          <div className="p-5 space-y-4 overflow-y-auto border-r border-slate-100 dark:border-slate-700">
            <div>
              <label
                htmlFor="report-paper-size"
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
              >
                Paper size
              </label>
              <select
                id="report-paper-size"
                value={paperSize}
                onChange={(event) => handlePaperSizeChange(event.target.value as PaperSize)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary/40 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100"
              >
                {PAPER_SIZES.map((size) => (
                  <option key={size.value} value={size.value}>
                    {size.label} — {size.hint}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Sections to include
              </p>
              <div className="space-y-2">
                {REPORT_SECTIONS.map((item) => {
                  const checked = formData.includes(item.KEY);
                  return (
                    <label
                      key={item.KEY}
                      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                        checked
                          ? "bg-indigo-50 dark:bg-primary/20 border-2 border-indigo-500 dark:border-primary"
                          : "bg-slate-50 dark:bg-slate-700/50 border-2 border-transparent hover:bg-slate-100 dark:hover:bg-slate-700"
                      }`}
                    >
                      <input
                        type="checkbox"
                        onChange={handleSectionChange}
                        name={item.label}
                        value={item.KEY}
                        checked={checked}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 rounded-md flex items-center justify-center ${
                          checked
                            ? "bg-indigo-500 dark:bg-primary text-white"
                            : "bg-white dark:bg-slate-600 border-2 border-slate-300 dark:border-slate-500"
                        }`}
                      >
                        {checked && (
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {item.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2 mt-2">
              <button
                type="button"
                onClick={handlePrint}
                disabled={printing}
                className="w-full inline-flex items-center justify-center gap-1.5 py-3 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 transition-all hover:bg-slate-50 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
              >
                <PrintIcon sx={{ fontSize: 16 }} />
                Print
              </button>
              <button
                type="submit"
                disabled={printing}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-primary to-primaryLight text-white text-sm font-medium hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-60"
              >
                Generate Report ({paperSize})
              </button>
            </div>
          </div>

          <div className="bg-slate-100 dark:bg-slate-900/40 overflow-auto p-4">
            <p className="text-xs font-semibold tracking-wide text-slate-500 mb-2 uppercase">
              Same layout as Manage Print
            </p>
            <CertificateCanvas
              page={previewPage}
              elements={template.elements}
              selectedId={null}
              previewData={previewData}
              previewTables={previewTables}
              previewCharts={previewCharts}
              previewChecklists={previewChecklists}
              interactive={false}
              zoom={printing ? 1 : 0.52}
              pageCount={reportPageCount(template)}
              paperRef={paperRef}
            />
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
}

export default ReportModal;
